import React, { ReactNode, useState, useEffect, useMemo } from 'react';
import { ChartComments } from './ChartComments';
import { supabaseClient } from '../../services/supabase';
import './ChartLayout.css';

// Grouped Chart Selector Component
interface GroupedChartSelectorProps {
  groups: {
    category: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  value: string;
  onChange: (value: string) => void;
}

const GroupedChartSelector: React.FC<GroupedChartSelectorProps> = ({ groups, value, onChange }) => {
  // Find which category the current value belongs to
  const activeCategory = useMemo(() => {
    for (const group of groups) {
      if (group.options.some(opt => opt.value === value)) {
        return group.category;
      }
    }
    return groups[0]?.category || '';
  }, [groups, value]);

  const activeGroup = groups.find(g => g.category === activeCategory);

  return (
    <div className="grouped-chart-selector">
      {/* Main category buttons */}
      <div className="chart-selector-categories">
        {groups.map(group => (
          <button
            key={group.category}
            className={`category-btn ${activeCategory === group.category ? 'active' : ''}`}
            onClick={() => {
              // Switch to first option in this category when category is clicked
              if (activeCategory !== group.category && group.options.length > 0) {
                onChange(group.options[0].value);
              }
            }}
          >
            {group.label}
          </button>
        ))}
      </div>
      {/* Sub-option buttons for active category */}
      {activeGroup && (
        <div className="chart-selector-options">
          {activeGroup.options.map(option => (
            <button
              key={option.value}
              className={`toggle-btn ${value === option.value ? 'active' : ''}`}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ChartLayoutProps {
  title: string;
  description?: string;
  chartType?: 'line' | 'bar' | 'doughnut' | 'pie' | 'map';
  keyMetrics?: ReactNode;
  chartContent: ReactNode;
  overviewContent?: ReactNode;
  relevanceContent?: ReactNode;
  equityContent?: ReactNode;
  methodologyContent?: ReactNode;
  activeView: string;
  onViewChange: (view: string) => void;
  chartSelector?: {
    options?: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
    grouped?: boolean;
    groups?: {
      category: string;
      label: string;
      options: { value: string; label: string }[];
    }[];
  };
  className?: string;
  showRelevanceEquity?: boolean;
  layout?: 'side-by-side' | 'vertical';
  // Download functionality
  onDownloadPNG?: () => void;
  onDownloadCSV?: () => void;
  pngFilename?: string;
  csvFilename?: string;
  // Data table functionality
  dataTableContent?: ReactNode;
  showDataTable?: boolean;
  onToggleDataTable?: () => void;
  // Comments functionality
  enableComments?: boolean;
  location?: string;
  // Additional button groups (for backcasting options, etc.)
  additionalButtonGroups?: {
    label: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
  }[];
}

export const ChartLayout: React.FC<ChartLayoutProps> = ({
  title,
  description,
  chartType = 'line',
  keyMetrics,
  chartContent,
  overviewContent,
  relevanceContent,
  equityContent,
  methodologyContent,
  activeView,
  onViewChange,
  chartSelector,
  className = '',
  showRelevanceEquity = false,
  layout = 'side-by-side',
  onDownloadPNG,
  onDownloadCSV,
  pngFilename = 'Chart',
  csvFilename = 'Data',
  dataTableContent,
  showDataTable = false,
  onToggleDataTable,
  enableComments = false,
  location,
  additionalButtonGroups
}) => {
  // Internal state for managing comments toggle
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);

  // Fetch comments count
  useEffect(() => {
    if (enableComments) {
      const fetchCount = async () => {
        const { count } = await supabaseClient
          .from('community_comments')
          .select('*', { count: 'exact', head: true })
          .eq('chart_context', title)
          .is('parent_id', null);
        setCommentsCount(count || 0);
      };
      fetchCount();
    }
  }, [enableComments, title]);

  // Handle custom data table toggle - close comments if open
  const handleToggleDataTable = () => {
    setShowComments(false);
    if (onToggleDataTable) {
      onToggleDataTable();
    }
  };

  // Handle comments toggle - close data table if open
  const handleToggleComments = () => {
    if (showDataTable && onToggleDataTable) {
      onToggleDataTable();
    }
    setShowComments(!showComments);
  };

  return (
    <div className={`chart-section ${className}`}>
      <div className="chart-content">
        <div className="chart-title-section">
          <h3>{title}</h3>
          {description && (
            <p 
              className="chart-description-text" 
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          
          {/* Key Metrics - right after title */}
          {keyMetrics && (
            <div className="key-metrics-after-title">
              {keyMetrics}
            </div>
          )}
        </div>
        
        {/* Conditional Layout: Side-by-side or Vertical */}
        {layout === 'side-by-side' ? (
          /* Side-by-side layout: Chart on left, Narrative on right */
          <div className={`chart-content-layout ${!(overviewContent || relevanceContent || equityContent || methodologyContent) ? 'single-column' : ''}`}>
            {/* Left Column - Chart */}
            <div className="chart-left-side">
              
              {/* Chart Type Selector (optional) - positioned directly above chart */}
              {chartSelector && (
                chartSelector.grouped && chartSelector.groups ? (
                  <GroupedChartSelector
                    groups={chartSelector.groups}
                    value={chartSelector.value}
                    onChange={chartSelector.onChange}
                  />
                ) : chartSelector.options ? (
                  <div className="chart-selector">
                    {chartSelector.options.map(option => (
                      <button
                        key={option.value}
                        className={`toggle-btn ${chartSelector.value === option.value ? 'active' : ''}`}
                        onClick={() => chartSelector.onChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null
              )}
              
              {/* Additional button groups (second row) - now dropdowns */}
              {additionalButtonGroups && additionalButtonGroups.length > 0 && (
                <div className="additional-button-groups">
                  {additionalButtonGroups.map((group, idx) => (
                    <div key={idx} className="additional-button-group">
                      <label className="additional-button-group-label">{group.label}:</label>
                      <select
                        className="additional-button-group-select"
                        value={group.value}
                        onChange={(e) => group.onChange(e.target.value)}
                      >
                        {group.options.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Chart container - provides consistent styling and loading placeholder */}
              <div className="chart-container">
                {chartContent}
              </div>

              {/* Buttons row - inside chart-left-side so they align with the chart */}
              {(onDownloadPNG || onDownloadCSV || onToggleDataTable || enableComments) && (
                <div className="chart-buttons-row chart-left-buttons">
                  <div className="left-buttons-group">
                    {onToggleDataTable && (
                      <button 
                        className="customize-data-btn"
                        onClick={handleToggleDataTable}
                        title="Customize Data"
                      >
                        {showDataTable ? 'Hide Data Table' : 'Customize Data'}
                      </button>
                    )}
                    {enableComments && (
                      <button 
                        className="comments-toggle-btn"
                        onClick={handleToggleComments}
                        title="View Comments"
                      >
                        💬 Comments {commentsCount > 0 && `(${commentsCount})`}
                      </button>
                    )}
                  </div>
                  {(onDownloadPNG || onDownloadCSV) && (
                    <div className="download-buttons-group">
                      {onDownloadPNG && (
                        <button 
                          className="chart-download-btn"
                          onClick={onDownloadPNG}
                          title={`Download ${pngFilename} as PNG`}
                        >
                          🖼️ PNG
                        </button>
                      )}
                      {onDownloadCSV && (
                        <button 
                          className="chart-download-btn"
                          onClick={onDownloadCSV}
                          title={`Download ${csvFilename} as CSV`}
                        >
                          📊 CSV
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Data table and comments - inside chart-left-side */}
              {showDataTable && <div className="data-table-wrapper">{dataTableContent}</div>}
              {showComments && <div className="comments-wrapper"><ChartComments chartName={title} location={location} visible={showComments} showToggle={false} /></div>}
            </div>

            {/* Right Column - Buttons and Content - Only render if there's content */}
            {(overviewContent || relevanceContent || equityContent || methodologyContent) ? (
              <div className="chart-right-side">
                {/* Toggle Buttons */}
                <div className="toggle-buttons">
                  {overviewContent && (
                    <button 
                      className={`toggle-btn ${activeView === 'overview' ? 'active' : ''}`}
                      onClick={() => onViewChange('overview')}
                    >
                      Overview
                    </button>
                  )}
                  {showRelevanceEquity && (
                    <>
                      <button 
                        className={`toggle-btn ${activeView === 'relevance' ? 'active' : ''}`}
                        onClick={() => onViewChange('relevance')}
                      >
                        Relevance
                      </button>
                      <button 
                        className={`toggle-btn ${activeView === 'equity' ? 'active' : ''}`}
                        onClick={() => onViewChange('equity')}
                      >
                        Equity
                      </button>
                    </>
                  )}
                  {methodologyContent && (
                    <button 
                      className={`toggle-btn ${activeView === 'methodology' ? 'active' : ''}`}
                      onClick={() => onViewChange('methodology')}
                    >
                      Methodology
                    </button>
                  )}
                </div>
                
                {/* Content Sections */}
                <div className="content-sections">
                  {/* Overview Section */}
                  {overviewContent && (
                    <div className={`content-section ${activeView === 'overview' ? '' : 'hidden'}`}>
                      {overviewContent}
                    </div>
                  )}
                  
                  {/* Relevance Section */}
                  {showRelevanceEquity && (
                    <div className={`content-section ${activeView === 'relevance' ? '' : 'hidden'}`}>
                      {relevanceContent}
                    </div>
                  )}
                  
                  {/* Equity Section */}
                  {showRelevanceEquity && (
                    <div className={`content-section ${activeView === 'equity' ? '' : 'hidden'}`}>
                      {equityContent}
                    </div>
                  )}
                  
                  {/* Methodology Section */}
                  {methodologyContent && (
                    <div className={`content-section ${activeView === 'methodology' ? '' : 'hidden'}`}>
                      {methodologyContent}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          /* Vertical layout: Middle: Chart, Bottom: Narrative */
          <div className="chart-vertical-layout">
            {/* Middle Section: Chart */}
            <div className="chart-section">
              {/* Chart Type Selector (optional) - positioned directly above chart */}
              {chartSelector && (
                chartSelector.grouped && chartSelector.groups ? (
                  <GroupedChartSelector
                    groups={chartSelector.groups}
                    value={chartSelector.value}
                    onChange={chartSelector.onChange}
                  />
                ) : chartSelector.options ? (
                  <div className="chart-selector">
                    {chartSelector.options.map(option => (
                      <button
                        key={option.value}
                        className={`toggle-btn ${chartSelector.value === option.value ? 'active' : ''}`}
                        onClick={() => chartSelector.onChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null
              )}
              
              {/* Additional button groups (second row) - now dropdowns */}
              {additionalButtonGroups && additionalButtonGroups.length > 0 && (
                <div className="additional-button-groups">
                  {additionalButtonGroups.map((group, idx) => (
                    <div key={idx} className="additional-button-group">
                      <label className="additional-button-group-label">{group.label}:</label>
                      <select
                        className="additional-button-group-select"
                        value={group.value}
                        onChange={(e) => group.onChange(e.target.value)}
                      >
                        {group.options.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Chart container - provides consistent styling and loading placeholder */}
              <div className="chart-container">
                {chartContent}
              </div>
            </div>
            
            {/* Bottom Section: Toggle Buttons + Content */}
            <div className="narrative-section">
              {/* Toggle Buttons */}
              <div className="toggle-buttons">
                {overviewContent && (
                  <button 
                    className={`toggle-btn ${activeView === 'overview' ? 'active' : ''}`}
                    onClick={() => onViewChange('overview')}
                  >
                    Overview
                  </button>
                )}
                {showRelevanceEquity && (
                  <>
                    <button 
                      className={`toggle-btn ${activeView === 'relevance' ? 'active' : ''}`}
                      onClick={() => onViewChange('relevance')}
                    >
                      Relevance
                    </button>
                    <button 
                      className={`toggle-btn ${activeView === 'equity' ? 'active' : ''}`}
                      onClick={() => onViewChange('equity')}
                    >
                      Equity
                    </button>
                  </>
                )}
                {methodologyContent && (
                  <button 
                    className={`toggle-btn ${activeView === 'methodology' ? 'active' : ''}`}
                    onClick={() => onViewChange('methodology')}
                  >
                    Methodology
                  </button>
                )}
              </div>
              
              {/* Content Sections */}
              <div className="content-sections">
                {/* Overview Section */}
                {overviewContent && (
                  <div className={`content-section ${activeView === 'overview' ? '' : 'hidden'}`}>
                    {overviewContent}
                  </div>
                )}
                
                {/* Relevance Section */}
                {showRelevanceEquity && (
                  <div className={`content-section ${activeView === 'relevance' ? '' : 'hidden'}`}>
                    {relevanceContent}
                  </div>
                )}
                
                {/* Equity Section */}
                {showRelevanceEquity && (
                  <div className={`content-section ${activeView === 'equity' ? '' : 'hidden'}`}>
                    {equityContent}
                  </div>
                )}
                
                {/* Methodology Section */}
                {methodologyContent && (
                  <div className={`content-section ${activeView === 'methodology' ? '' : 'hidden'}`}>
                    {methodologyContent}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Content components for consistent structure
export const OverviewContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => (
  <>
    <h4>{title}</h4>
    <p dangerouslySetInnerHTML={{ __html: description }} />
    {children && (
      <div className="key-metrics">
        <h5>Key Insights:</h5>
        <div className="insights-content">
          {children}
        </div>
      </div>
    )}
  </>
);

export const RelevanceContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => (
  <>
    <h4>{title}</h4>
    <p dangerouslySetInnerHTML={{ __html: description }} />
    {children}
  </>
);

export const EquityContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => (
  <>
    <h4>{title}</h4>
    <p dangerouslySetInnerHTML={{ __html: description }} />
    {children}
  </>
);

export const MethodologyContent: React.FC<{
  title: string;
  description: string;
  dataSources: string[];
  methodology: string[];
}> = ({ title, description, dataSources, methodology }) => (
  <>
    <h4>{title}</h4>
    <p>{description}</p>
    
    <div className="methodology-details">
      <h5>Data Sources:</h5>
      <ul>
        {dataSources.map((source, index) => (
          <li key={index} dangerouslySetInnerHTML={{ __html: source }} />
        ))}
      </ul>
      
      <h5>Methodology:</h5>
      <ul>
        {methodology.map((item, index) => (
          <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </ul>
    </div>
  </>
);
